/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Select, SelectItem, Spinner, User, CircularProgress, Input, Modal, ModalHeader, ModalContent, ModalBody, ModalFooter } from '@nextui-org/react';
import { IconArrowLeft } from '@tabler/icons-react';
import { NumericFormat } from 'react-number-format';
import toast from 'react-hot-toast';
import moment from 'moment';
import { classNames, Constants, copy, formatAmount, fromPhotos } from '../../../../utils';
import { TeacherAttendeceService, TeacherTestsService } from '../../../../services';
import Render from '../../../../components/render';


const QualificationsCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showIndicators, setShowIndicators] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { filterBy, form, grades, isLoading, isValidForm, onChange, sections, onSubmit, lapses, tests, maxQuantitativeScale, scales } = useFetchTable(location.state);

  return (
    <div className="flex gap-4">
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <IndicatorsModal
        test={selectedItem}
        isOpen={showIndicators}
        onClose={() => setShowIndicators(false)}
      />

      <aside className="bg-foreground-200 w-[45%] h-full p-4 rounded-lg flex flex-col gap-4">
        <div className="flex gap-4">
          <IconArrowLeft className="cursor-pointer" onClick={() => navigate('/qualifications', { replace: true })} />
          <p className="font-bold text-secondary capitalize">{location?.state?.name} - {location?.state?.school_grade?.name}</p>
        </div>

        <Filters
          filterBy={filterBy}
          grades={grades}
          sections={sections}
          lapses={lapses}
        />

        <div className='grid grid-cols-2 gap-4 mt-4'>
          {
            tests.map((test) => (
              <EvaluationCard
                item={test}
                key={test.id}
                onChange={onChange}
                form={form}
                filterBy={filterBy}
                maxQuantitativeScale={maxQuantitativeScale}
                onShowIndicators={() => {
                  setSelectedItem(test);
                  setShowIndicators(true);
                }}
              />
            ))
          }
        </div>

        <div className='flex w-full justify-center items-center'>
          <Button
            variant="flat"
            className="bg-green text-white font-semibold w-[20rem]"
            onPress={onSubmit}
            isDisabled={!isValidForm(false)}
          >
            Guardar
          </Button>
        </div>
      </aside>

      <main className="text-sm w-[50%] last:rounded-b-lg last:overflow-hidden">
        <div
          className='grid grid-cols-4 rounded-t-lg bg-foreground-500 text-white [&>div]:p-2'
        >
          <div className="th font-semibold col-span-2 pl-2">Estudiantes</div>
          <div className="th font-semibold text-center">Nota</div>
          <div className="th font-semibold text-center">Total</div>
        </div>
        {form.students.map((user, userIdx) => {
          const wasEvaluated = user.evaluated;

          return (
            <div
              key={user.student_id}
              className="grid grid-cols-4 [&>div]:px-2 border-t border-t-foreground-300"
            >
              <div className="col-span-2 bg-foreground-200 py-2">
                <User
                  avatarProps={{ showFallback: true, radius: 'full', src: user?.photo ? fromPhotos(user.photo) : null }}
                  name={`${user?.name} ${user?.lastname}`}
                  classNames={{ description: 'text-foreground-500' }}
                />
              </div>
              <div className="flex justify-center items-center">
                <Render condition={!wasEvaluated}>
                  <Render condition={location.state?.type_qualification === Constants.SUBJECTS.TYPE_QUALIFICATION.QUANTITATIVE}>
                    <NumericFormat
                      displayType="input"
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      allowNegative={false}
                      value={user.score ?? ''}
                      onValueChange={v => onChange(v.value, 'score', userIdx)}
                      customInput={Input}
                      // A partir de acá se establecen las props del input personalizado (Input)
                      aria-label="Nota"
                      variant="bordered"
                      radius="sm"
                    />
                  </Render>

                  <Render condition={location.state?.type_qualification === Constants.SUBJECTS.TYPE_QUALIFICATION.QUALITATIVE}>
                    <Select
                      aria-label="Nota"
                      placeholder="Selecciona"
                      variant="bordered"
                      radius="sm"
                      disallowEmptySelection={true}
                      selectedKeys={!!user.score ? [user.score] : []}
                      onSelectionChange={v => onChange(v.currentKey, 'score', userIdx)}
                      items={scales}
                    >
                      {(item) => <SelectItem>{ item.label }</SelectItem>}
                    </Select>
                  </Render>
                </Render>

                <Render condition={wasEvaluated}>
                  <p className='text-lg'>{ user.score_text }</p>
                </Render>
              </div>
              <div className="flex justify-center items-center">
                <p className='text-lg'>{ user.total_score }</p>
              </div>
            </div>
          );
        })}
      </main>
    </div>
  )
}

const IndicatorsModal = ({ isOpen, onClose, test }) => {
  const location = useLocation();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>Indicadores</ModalHeader>

        <ModalBody>
          <p className="p-2 text-white font-semibold bg-blue rounded-lg capitalize">
            { location?.state?.school_grade?.name }
          </p>
          <p className="p-2 text-white font-semibold bg-gray rounded-lg capitalize">
            { location?.state?.name }
          </p>

          <p>Nombre: { test?.name }</p>
          <p>Fecha: { moment(test?.date).format('DD [de] MMMM YYYY') }</p>
          <p>Indicador: { test?.indicator_text ?? 'No definido' }</p>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const EvaluationCard = ({ item, onChange, form, filterBy, maxQuantitativeScale, onShowIndicators }) => {
  const currentTest = form.test_question_id === item.id ? item : null

  const onSelectTest = (test) => {
    filterBy(test.id, 'test_question_id')
    onChange(test.id, 'test_question_id')
  }

  return (
    <div
      className={classNames(
        'p-4 rounded-lg flex flex-col cursor-pointer',
        currentTest ? 'bg-green/20' : 'bg-white'
      )}
      onClick={() => onSelectTest(item)}
    >
      <p>{item?.name}</p>
      <p className="text-xs font-bold">{moment(item?.date).format('DD [de] MMMM YYYY')}</p>
      <div className="flex justify-between">
        <p className="mt-2 text-sm text-primary font-bold">
          %{item?.percentage}
        </p>
        <p className="mt-2 text-sm text-primary font-bold underline" onClick={onShowIndicators}>
          Indicadores
        </p>
      </div>
      <div className="flex items-center  gap-4 mt-2">
        <CircularProgress
          aria-label="Calificación promedio"
          className="w-16 h-16"
          formatOptions={{ style: 'decimal' }}
          classNames={{
            svg: "w-16 h-36",
            indicator: "stroke-green",
            value: "text-md",
          }}
          showValueLabel={true}
          strokeWidth={4}
          value={item?.section_average}
          maxValue={maxQuantitativeScale}
        />
        <p className='text-sm'>Promedio de la sección</p>
      </div>
    </div>
  )
}

const Filters = ({ filterBy, grades, sections, lapses }) => {
  const initialFilter = {
    school_grade_id: '',
    school_section_id: '',
    school_lapse_id: '',
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }


  return (
    <section className='flex items-center gap-4'>
      <Select
        label="Sección"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        classNames={{
          label: 'font-semibold',
          base: 'w-full',
          trigger: 'border-1',
        }}
        disallowEmptySelection={true}
        selectedKeys={!!form.school_section_id ? [form.school_section_id] : []}
        onSelectionChange={v => onChange(v.currentKey, 'school_section_id')}
      >
        {sections.map((item) => (
          <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
        ))}
      </Select>

      <Select
        label="Lapso"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        classNames={{
          label: 'font-semibold',
          base: 'w-full',
          trigger: 'border-1',
        }}
        disallowEmptySelection={true}
        selectedKeys={!!form.school_lapse_id ? [form.school_lapse_id] : []}
        onSelectionChange={v => onChange(v.currentKey, 'school_lapse_id')}
      >
        {lapses.map((item) => (
          <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
        ))}
      </Select>
    </section>
  )
}

const useFetchTable = (subject) => {
  const initialFilters = {
    page: 1,
    school_grade_id: subject?.school_grade_id,
    school_year_id: subject?.school_grade?.school_year_id,
    school_section_id: '',
    school_lapse_id: '',
    test_question_id: '',
  };
  const initialForm = {
    subject_id: subject?.id,
    school_grade_id: subject?.school_grade_id,
    school_year_id: subject?.school_grade?.school_year_id,
    school_section_id: '',
    school_lapse_id: '',
    test_question_id: '',
    students: [],
  };
  const user = useSelector(state => state.user);
  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [form, setForm] = useState(initialForm);
  const [lapses, setLapses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sections, setSections] = useState([]);
  const [scales, setScales] = useState([]);
  const [maxQuantitativeScale, setMaxQuantitativeScale] = useState(0);
  const [tests, setTests] = useState([])

  const toSelect = (items) => items.map(x => ({ ...x, value: String(x.id), label: x.name }));

  const fetchData = async () => {
    if (!canFetch || !filters.test_question_id || !filters.school_section_id) return;
    setCanFetch(false);

    try {
      const response = await TeacherTestsService.tests.getStudentsScore({
        test_question_id: +filters.test_question_id,
        school_lapse_id: +form.school_lapse_id,
        school_section_id: +form.school_section_id,
        subject_id: subject.id,
      })

      setForm(s => ({
        ...s,
        students: response.map(student => ({
          ...student,
          score: student.evaluated ? formatAmount(student?.score) : '',
          total_score: formatAmount(student?.total_score)
        }))
      }));
      setCanFetch(true);

    } catch (error) {
      onError(String(error));
      setCanFetch(true);
    }
  }

  const fetchTests = async () => {
    if (!form.school_lapse_id || !form.school_section_id || !form.subject_id) return;
    try {
      if (!canFetch) return;
      setCanFetch(false);

      const response = await TeacherTestsService.tests.getQuestionsAvg({
        professor_id: user.id,
        school_lapse_id: +form.school_lapse_id,
        school_section_id: +form.school_section_id,
        subject_id: +form.subject_id,
      })

      setTests(response)

      setCanFetch(true);
    } catch (error) {
      onError(String(error));
      setCanFetch(true);
    }
  }

  const fetchSelects = async () => {
    try {
      const selects = await TeacherAttendeceService.attendence.getSelects({ professor_id: user.id });
      setGrades(toSelect(selects.grades));
      setLapses(toSelect(selects.lapses));
      setSections(toSelect(selects.sections));
      setScales(selects.scales.map(x => ({ label: x.letter, key: String(x.to) })));
      setMaxQuantitativeScale(selects.maxQuantitativeScale)
      setCanFetch(true)

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    }

    if (form.students.filter(x => !x.evaluated).length === 0) {
      return onError('No hay estudiantes sin evaluar');
    }

    if (!form.school_grade_id) {
      return onError('Debe seleccionar el grado');
    }

    if (!form.school_section_id) {
      return onError('Debe seleccionar la sección');
    }

    if (!form.school_lapse_id) {
      return onError('Debe seleccionar el lapso');
    }

    if (form.students.some(x => !x.score)) {
      return onError('Debe enviar un puntaje para cada estudiante');
    }

    if (form.students.some(x => x.score > maxQuantitativeScale)) {
      return onError(`El puntaje máximo es ${maxQuantitativeScale}`);
    }

    return true;
  }

  const onChange = (value, target, index) => {
    if (target === 'score') {
      const _form = copy(form);
      _form['students'][index][target] = _form['students'][index][target] === value ? null : value;
      return setForm(_form);
    }

    setForm(s => ({ ...s, [target]: value }));
  }

  const onError = (msg) => toast.error(msg);

  const filterBy = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    setFilters({ ...filters, page: 1, [target]: value })
  }

  const onSubmit = async () => {
    if (!isValidForm()) return;
    try {

      const data = {
        ...form,
        professor_id: user.id,
        // Omite los estudiantes que ya han sido evaluados
        students: form.students.filter(x => !x.evaluated).map((x) => ({
          student_id: x.student_id,
          score: x.score,
        }))
      }

      await TeacherTestsService.tests.create(data);
      toast.success('Puntaje tomado correctamente');
      fetchData();
      fetchTests()
    } catch (error) {
      onError(String(error));
    }
  }

  useEffect(() => {
    fetchData();
  }, [filters.test_question_id, filters.school_section_id]);

  useEffect(() => {
    fetchTests();
  }, [filters.school_grade_id, filters.school_section_id, filters.school_lapse_id]);

  useEffect(() => {
    fetchSelects();
  }, [])


  return {
    data,
    filterBy,
    filters,
    form,
    onChange,
    isLoading: !canFetch,
    isValidForm,
    sections: sections.filter(x => x.school_grade_id === +form.school_grade_id),
    onSubmit,
    grades,
    lapses: lapses.filter(x => x.school_year_id === +form.school_year_id),
    tests,
    scales,
    maxQuantitativeScale,
  }
}

export default QualificationsCreate;